/**
 * Horizontal density scrubber for a bounded time window (defaults to 24h).
 *
 * Two independent interactions:
 * - Hover (desktop) or focus (keyboard) an hour bucket to preview it — fires
 *   `onScrub`, transient, reverts once the pointer/focus leaves.
 * - Click or tap a bucket to select it — fires `onSelect`, persists until
 *   another bucket is selected or the same one is clicked again to clear it.
 *   This is the value real consumers should filter on (e.g. "which hour's
 *   OIs to show"), since a hover shouldn't itself trigger a data filter.
 *
 * The floating tooltip (see `TooltipConnector`) is strictly a
 * hover/focus readout — it is never shown for the selection on its own.
 * A selection can persist indefinitely, and parking a label over the map
 * for that whole time obscures the very tiles the user selected an hour to
 * inspect. The selected hour instead reads as a solid, full-strength bar.
 *
 * Buckets are discrete — one per hour — rather than a continuous,
 * cursor-following scrub. That's an intentional simplification called out in
 * the design note that shipped with this component's mock-up, not a gap.
 *
 * Bar height is driven by a **fixed, shared reference scale** —
 * `baselineMax` (default `10`) — rather than each dataset's own max (see
 * `scale.ts`). A bucket's height is `value / baselineMax` (clamped to
 * 100%), so:
 * - 0 results → 0% height
 * - 5 results → 50% height (against the default baseline of 10)
 * - 10+ results → 100% height (clamped — 12 and 2500 render identically)
 *
 * This is deliberate: independently normalizing every dataset to its own
 * max would make a "2 results" bucket look just as tall as a "2500 results"
 * bucket in different charts, which is misleading when comparing hours (or
 * separate scrubbers) at a glance. `baselineMax` is today's assumed
 * typical worst case per hour — raise it if that assumption changes.
 */
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { AxisEndLabel } from './AxisEndLabel';
import { Bucket } from './Bucket';
import { SelectedHourLabel } from './SelectedHourLabel';
import { TooltipConnector } from './TooltipConnector';
import { DEFAULT_BASELINE_MAX, REVEAL_ZONE_HEIGHT_PX, normalizeHeight } from './scale';
import { SURFACE_VARS } from './surface';
import { HOUR_MS, floorToHour, formatHour } from './time';
import type { TimelineScrubberProps } from './types';

export function TimelineScrubber({
  buckets,
  durationHours = 24,
  defaultSelectedIndex = 0,
  showUTC = true,
  baselineMax = DEFAULT_BASELINE_MAX,
  scale = 'linear',
  now: nowOverride,
  surface = 'default',
  onScrub,
  onSelect,
  className,
}: TimelineScrubberProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(defaultSelectedIndex);
  const [wallClockNow, setWallClockNow] = useState(() => new Date());

  // Keeps the reference clocks accurate for as long as the scrubber stays
  // mounted. Hour-level granularity means a 1-minute tick is plenty. Skipped
  // entirely when `now` is overridden — that override is meant to be a
  // frozen point in time (see `TimelineScrubberProps.now`), not something
  // this should tick past on its own.
  useEffect(() => {
    if (nowOverride) return;
    const id = setInterval(() => setWallClockNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [nowOverride]);

  const now = nowOverride ?? wallClockNow;

  // Hover/focus alone drives the tooltip — it deliberately does NOT fall
  // back to `selectedIndex`, so the readout appears while the user is
  // pointing at an hour and clears the moment they leave.
  const displayed = hoverIndex === null ? null : (buckets[hoverIndex] ?? null);
  const displayedPct = hoverIndex === null ? 0 : ((hoverIndex + 0.5) / buckets.length) * 100;
  const displayedHeightRatio = displayed ? normalizeHeight(displayed.count, baselineMax, scale) : 0;

  // The selection is tracked separately from the hover readout above: it has
  // its own persistent label under the track (see `SelectedHourLabel`).
  const selected = selectedIndex === null ? null : (buckets[selectedIndex] ?? null);
  const selectedPct = selectedIndex === null ? 0 : ((selectedIndex + 0.5) / buckets.length) * 100;

  // Every clock readout (start, end, hover) is derived from the same
  // hour-floored reference point so they stay aligned to the same grid.
  const flooredNow = floorToHour(now, showUTC);
  const nowLabel = formatHour(flooredNow, showUTC);
  const endLabel = formatHour(new Date(flooredNow.getTime() + durationHours * HOUR_MS), showUTC);
  const displayedClock = displayed
    ? formatHour(new Date(flooredNow.getTime() + displayed.offsetHours * HOUR_MS), showUTC)
    : null;

  const handleHoverStart = (index: number) => {
    setHoverIndex(index);
    onScrub?.(buckets[index] ?? null);
  };
  const handleHoverEnd = () => {
    setHoverIndex(null);
    onScrub?.(null);
  };
  const handleSelect = (index: number) => {
    const next = index === selectedIndex ? null : index;
    setSelectedIndex(next);
    onSelect?.(next === null ? null : (buckets[next] ?? null));
  };

  return (
    // Row, not a stack: the window's endpoint readouts cap either side of
    // the track, each centered on the timeline's own horizontal axis (see
    // `AxisEndLabel`), so the track itself takes whatever width is left.
    <div
      data-surface={surface}
      className={cn('flex w-full select-none items-start gap-3', SURFACE_VARS[surface], className)}
    >
      <AxisEndLabel offsetLabel="T+0:00" clockLabel={nowLabel} align="start" />

      <div className="flex flex-1 flex-col">
        {/* Reveal zone: the vertical space the tooltip and its guide line
            get to float in — see `REVEAL_ZONE_HEIGHT_PX` for why its height
            is what governs the clearance above a full-height bar. Nothing
            up here is interactive; the hit areas are the short strip pinned
            to its bottom. */}
        <div className="relative" style={{ height: `${REVEAL_ZONE_HEIGHT_PX}px` }}>
          {displayed && displayedClock && (
            <TooltipConnector
              bucket={displayed}
              clockLabel={displayedClock}
              leftPct={displayedPct}
              heightRatio={displayedHeightRatio}
            />
          )}
          {/* Hit-area row: a short strip pinned to the bottom of the zone
              rather than the whole thing. It still spans the full 40px bar
              band (plus 4px of grab room, for a 44px touch target), so a
              zero-result hour stays exactly as easy to hover/click as the
              tallest bucket — but the pointer no longer arms the scrubber
              from way up in the tooltip's airspace, which on a map overlay
              means a large invisible region swallowing hovers meant for
              the tiles behind it.

              Hover/focus-leave are handled here, not per-bucket: React's
              mouseenter/mouseleave don't fire for child-to-child
              transitions within the same parent, so moving the pointer
              between adjacent buckets only fires the new bucket's
              onMouseEnter — hoverIndex never passes through `null` in
              between, which is what keeps the tooltip mounted (and thus
              animatable) while sliding across buckets instead of
              flickering out and back in. Same idea for onBlur via focus's
              relatedTarget. */}
          <div
            className="absolute inset-x-0 bottom-0 flex h-11 items-stretch gap-px"
            onMouseLeave={handleHoverEnd}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                handleHoverEnd();
              }
            }}
          >
            {buckets.map((bucket, i) => (
              <Bucket
                key={bucket.offsetHours}
                bucket={bucket}
                heightRatio={normalizeHeight(bucket.count, baselineMax, scale)}
                isDisplayed={i === hoverIndex}
                isSelected={i === selectedIndex}
                surface={surface}
                onHoverStart={() => handleHoverStart(i)}
                onSelect={() => handleSelect(i)}
              />
            ))}
          </div>
        </div>

        {/* Reserved strip under the track for the selection's readout. Kept
            at a fixed height whether or not anything is selected, so
            clearing the selection doesn't make the whole component jump. */}
        <div className="relative h-3.5">
          {selected && (
            <SelectedHourLabel offsetLabel={`T+${selected.offsetHours}:00`} leftPct={selectedPct} />
          )}
        </div>
      </div>

      <AxisEndLabel offsetLabel={`T+${durationHours}:00`} clockLabel={endLabel} align="end" />
    </div>
  );
}
